import classNames from 'classnames'
import type { CSSProperties } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getAccessibilityTags } from '../../config'
import { usePaginationDots } from './hooks'
import PaginationDot from './PaginationDot'
import styles from './PaginationDots.module.scss'
import { PaginationDotsVariant } from './types'
import type { PaginationDotsEdgeType, PaginationDotsIndex, PaginationDotsProps } from './types'

const TRANSITION_PROCESS_DURATION = 480

const PaginationDots = ({
  'aria-label': ariaLabel,
  className,
  pages = 1,
  index,
  onClick,
  progress = 0,
  rtl = false,
  getDotAriaLabel,
  variant = PaginationDotsVariant.manual,
}: PaginationDotsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [displayProgress, setDisplayProgress] = useState(0)
  const { scrollToCenter, validateIndex, getEdgePositions, translateX } = usePaginationDots()
  const [indexValidated, setIndexValidated] = useState<PaginationDotsIndex>({
    current: validateIndex(index, pages),
    previous: undefined,
  })

  const edgePositions = getEdgePositions(pages, indexValidated.current)

  const { getDotReference, slides: slidesAriaLabel } = getAccessibilityTags()

  useEffect(() => {
    setIndexValidated((previousIndex) => {
      return { current: validateIndex(index, pages), previous: previousIndex.current }
    })
  }, [index, pages])

  useEffect(() => {
    if (progress % 10 === 0) {
      setDisplayProgress(progress)
    }
  }, [progress])

  useLayoutEffect(() => {
    const scrollToCenterTimeout = setTimeout(() => {
      scrollToCenter(containerRef, indexValidated.current, rtl)
    }, TRANSITION_PROCESS_DURATION)
    return () => {
      scrollToCenterTimeout && clearTimeout(scrollToCenterTimeout)
    }
  }, [indexValidated, scrollToCenter, containerRef, rtl])

  const handleClick = useCallback(
    (indexClicked: number, edgePositions: PaginationDotsEdgeType) => {
      if (!onClick) return
      const newIndex =
        indexClicked === edgePositions.first
          ? 0
          : indexClicked === edgePositions.last
            ? pages - 1
            : indexClicked
      onClick(newIndex)
    },
    [onClick, pages]
  )

  const progressCssVariable =
    variant === PaginationDotsVariant.automatic
      ? ({
          '--pintxo-pagination-dots-progress-bar-value': `${displayProgress}%`,
        } as CSSProperties)
      : {}

  const transformX = { transform: `translateX(${translateX}px)` }
  return (
    <div
      className={classNames(styles['pintxo-pagination-dots'], className)}
      data-rtl={rtl}
      data-variant={variant}
      style={progressCssVariable}
      ref={containerRef}
    >
      <div
        className={styles['pintxo-pagination-dots__container']}
        style={transformX}
        role="tablist"
        aria-label={ariaLabel || slidesAriaLabel}
      >
        {Array.from({ length: pages }, (_, i) => i).map((mapIndex) => {
          return (
            <PaginationDot
              aria-label={
                (getDotAriaLabel && getDotAriaLabel(mapIndex)) || getDotReference(mapIndex)
              }
              key={mapIndex}
              edge={mapIndex === edgePositions.first || mapIndex === edgePositions.last}
              onClick={() => handleClick(mapIndex, edgePositions)}
              selected={mapIndex === indexValidated.current}
              rtl={rtl}
              variant={variant}
              unselected={mapIndex === indexValidated.previous}
            />
          )
        })}
      </div>
    </div>
  )
}

export default PaginationDots
